import { useState, useEffect, useRef, useMemo } from 'react';
import type { CompletedTask } from '../services/api';
import { storeManagersApi } from '../services/api';
import { flexibleTimeToISO, clampStoreMinutes } from '../utils/visitTime';
import { evenSplitMinutes } from '../utils/timeAllocation';
import type { VisitOrderReview } from '../utils/visitOrderSummary';

export interface ProjectSummary {
  projectId: number;
  projectType: 'call' | 'set';
  projectName: string;
  clientName?: string;
  timeMinutes: number;
  status: 'complete' | 'incomplete';
  // Enhanced fields for multi-task visits
  startTime?: string;  // ISO datetime
  endTime?: string;    // ISO datetime
}

// Helper to convert CompletedTask array to ProjectSummary array
export function completedTasksToProjectSummaries(tasks: CompletedTask[]): ProjectSummary[] {
  return tasks.map(task => ({
    projectId: task.project_id,
    projectType: task.task_type,
    projectName: task.project_description,
    clientName: task.client_name || undefined,
    timeMinutes: task.duration_minutes,
    status: 'complete' as const,
    startTime: task.start_time,
    endTime: task.end_time,
  }));
}

export interface CheckoutData {
  managerName: string;
  signatureDataUrl: string | null;
  noSignatureReason: string | null;
  projects: ProjectSummary[];
  totalTimeMinutes: number;
  timestamp: string;
  // Team project fields
  isTeamProject: boolean;
  isTeamLeader: boolean;
  // Visit notes
  visitNotes: string;
  /**
   * Odometer as the rep leaves. Pre-filled with their last reading (the
   * vehicle hasn't moved while they were inside), so accepting it is one tap.
   * It closes this visit and starts the next drive. Undefined if skipped.
   */
  departureOdometer?: number;
  // Store times (editable)
  storeCheckInTime?: string;   // ISO datetime
  storeCheckOutTime?: string;  // ISO datetime
  // #2104 Split Time (feature-gated): per-project minute allocation partitioning
  // the visit span. null = default mode (actual task-timer times, unchanged
  // behavior). Only ever non-null when the split_time_at_checkout gate is on.
  timeAllocation?: { projectId: number; minutes: number }[] | null;
}

interface ManagerOption {
  id: number;
  manager_name: string;
  use_count: number;
}

interface CheckoutSummaryProps {
  storeId: number;
  storeName: string;
  projects: ProjectSummary[];
  totalTimeMinutes: number;
  onComplete: (data: CheckoutData) => void;
  onCancel: () => void;
  isOpen: boolean;
  // #2092 — checkout signature gate (project-level, REQUIRED BY DEFAULT).
  // true: Skip Signature is not rendered; completing demands a manager name +
  // a drawn signature. false: legacy behavior (skip allowed with a reason,
  // manager name optional on the skip path).
  requiresSignature?: boolean;
  // Optional initial values (for manual entries that already captured this data)
  // #2959 / #3022 — Team Project / Team Leader are DETERMINISTIC, from the set
  // assignment (lead = associateid, members = rep1/rep2). They render as
  // READ-ONLY indicators, never rep-editable toggles, and are hidden entirely
  // for a non-team visit. These carry the resolved values in; the component
  // does not let the rep change them.
  initialTeamProject?: boolean;
  initialTeamLeader?: boolean;
  initialNotes?: string;
  // Store times for display/edit
  storeCheckInTime?: string;   // ISO datetime - when user checked in
  storeCheckOutTime?: string;  // ISO datetime - defaults to now, user can adjust
  /**
   * The rep's last odometer reading, used to pre-fill the departure reading.
   * Omit to hide the odometer field (manual/backfill entries).
   */
  lastOdometer?: number | null;
  // #2340 — manager-review data keyed by projectId: orders + qty, out-of-stock,
  // and on-hand counts captured this visit, shown under each task so the manager
  // can review the work before signing.
  orderReview?: VisitOrderReview;
  // #2104 — Split Time feature gate (django-flags split_time_at_checkout,
  // default OFF). false (default): check-in/out times render READ-ONLY (only the
  // tracked time), no Split button — checkout submits the actual timer times.
  // true: check-in/out become editable and a "Split Time Evenly" control lets the
  // rep allocate the visit span across tasks (no-overlap partition).
  splitTimeEnabled?: boolean;
}

// #2340 — reps only count "lows" (3 or under) and outs; on-hand at/under this
// gets a LOW badge so the manager sees what needs ordering.
const LOW_STOCK_THRESHOLD = 3;

const NO_SIGNATURE_REASONS = [
  { value: 'manager_unavailable', label: 'Manager not available' },
  { value: 'store_closed_early', label: 'Store closed early' },
  { value: 'emergency_departure', label: 'Emergency departure' },
  { value: 'other', label: 'Other' },
];

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// Seconds-precise duration for a task row (#1996). The IDB session model
// rounds each session to whole minutes, so sub-minute tasks rendered a
// confusing "0m" right next to a real "8:52 - 8:53" range, and the per-row
// minutes no longer summed to the separately-computed total. We derive the
// row duration from its OWN start/end span (the values shown beside it) so
// the number is honest and the rows sum to the total by construction. Falls
// back to the stored minutes when the span is unavailable. Display-only —
// submission still ships the IDB records unchanged.
function taskDurationSeconds(p: ProjectSummary): number {
  if (p.startTime && p.endTime) {
    const s = new Date(p.startTime).getTime();
    const e = new Date(p.endTime).getTime();
    if (!isNaN(s) && !isNaN(e) && e >= s) return Math.round((e - s) / 1000);
  }
  return Math.max(0, p.timeMinutes) * 60;
}

function formatDurationSeconds(totalSec: number): string {
  if (totalSec < 60) return `${totalSec}s`;
  return formatDuration(Math.round(totalSec / 60));
}

// Helper: returns a valid Date or null. Carrying around Invalid Date causes
// downstream crashes (toISOString throws, getTime returns NaN). #1947 follow-up.
function safeDate(input: string | undefined | null): Date | null {
  if (!input) return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

// #2465 — wall-clock label or null (never "Invalid Date"). A malformed task
// end-time (manual-entry edge case) used to render "12:30 PM - Invalid Date"
// in the summary; callers show just the parseable side instead.
function formatClock(iso: string | undefined | null): string | null {
  const d = safeDate(iso);
  return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
}

// Helper: ISO datetime -> 24-hour "HH:MM" for native <input type="time">.
// Returns '' when the input is invalid so the picker shows blank (and the
// user can pick from scratch).
function isoToHHMM(iso: string | undefined | null): string {
  const date = safeDate(iso);
  if (!date) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

// Helper: merge "HH:MM" from a native time picker back onto the reference
// date (so we preserve the original day). Falls back to "today" when the
// reference is invalid — otherwise toISOString throws on Invalid Date,
// which silently dropped the rep's edits in diag #28.
function hhmmToISO(hhmm: string, referenceISO: string | undefined | null): string | null {
  const match = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  if (hours > 23 || mins > 59) return null;
  const ref = safeDate(referenceISO) ?? new Date();
  ref.setHours(hours, mins, 0, 0);
  return ref.toISOString();
}

export default function CheckoutSummary({
  storeId,
  storeName,
  projects,
  totalTimeMinutes,
  onComplete,
  onCancel,
  isOpen,
  requiresSignature = true,
  initialTeamProject,
  initialTeamLeader,
  initialNotes,
  storeCheckInTime,
  storeCheckOutTime,
  lastOdometer,
  orderReview,
  splitTimeEnabled = false,
}: CheckoutSummaryProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [managerName, setManagerName] = useState('');
  const [managerOptions, setManagerOptions] = useState<ManagerOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [showNoSignatureModal, setShowNoSignatureModal] = useState(false);
  const [noSignatureReason, setNoSignatureReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  // #2104 Split Time allocation (only ever engaged when splitTimeEnabled). null =
  // default mode (today's behavior). Once engaged, per-project minutes must sum
  // to the visit span before Complete enables. allocTouched tracks hand-edits so
  // a span edit only auto re-splits untouched (purely even) allocations.
  const [allocMinutes, setAllocMinutes] = useState<Record<number, number> | null>(null);
  const allocTouched = useRef(false);

  // Editable store times. Normalize to ISO via the shared lenient parser so a
  // TIME-ONLY server string ("20:34:00") still populates the picker instead of
  // rendering blank (#2017) — the old strict `safeDate` gate rejected anything
  // `new Date()` couldn't parse, which is exactly how the check-in field went
  // empty. Unparseable input still collapses to '' (blank picker), so the
  // diag #28 Invalid-Date guard is preserved.
  const [checkInTime, setCheckInTime] = useState<string>(() => flexibleTimeToISO(storeCheckInTime));
  const [checkOutTime, setCheckOutTime] = useState<string>(
    () => flexibleTimeToISO(storeCheckOutTime) || new Date().toISOString()
  );
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Team project fields - use initial values if provided
  const [isTeamProject, setIsTeamProject] = useState(initialTeamProject ?? false);
  const [isTeamLeader, setIsTeamLeader] = useState(initialTeamLeader ?? false);
  // Visit notes - use initial value if provided
  const [visitNotes, setVisitNotes] = useState(initialNotes ?? '');
  const [departureOdometer, setDepartureOdometer] = useState(
    lastOdometer != null ? lastOdometer.toFixed(1) : ''
  );

  const incompleteCount = projects.filter(p => p.status === 'incomplete').length;

  // Reset state when modal opens (with initial values if provided)
  useEffect(() => {
    if (isOpen) {
      setIsTeamProject(initialTeamProject ?? false);
      setIsTeamLeader(initialTeamLeader ?? false);
      setVisitNotes(initialNotes ?? '');
      // Reset store times — normalize via the shared parser so time-only
      // values populate; unparseable input still collapses to '' (blank picker).
      setCheckInTime(flexibleTimeToISO(storeCheckInTime));
      setCheckOutTime(flexibleTimeToISO(storeCheckOutTime) || new Date().toISOString());
      setDepartureOdometer(lastOdometer != null ? lastOdometer.toFixed(1) : '');
      // #2104: never carry an allocation across visits.
      setAllocMinutes(null);
      allocTouched.current = false;
    }
  }, [isOpen, initialTeamProject, initialTeamLeader, initialNotes, storeCheckInTime, storeCheckOutTime, lastOdometer]);

  // Calculate total store time from check-in/out times. Falls back to the
  // upstream task-total when either timestamp is missing/invalid, and is
  // clamped to never go negative — a check-in (wrongly) recorded after
  // checkout used to surface "-60m" in the summary (#2017 / #1947 follow-up).
  const calculateStoreTime = (): number =>
    clampStoreMinutes(checkInTime, checkOutTime, totalTimeMinutes);

  // #3028 — collapse multiple work sessions for the SAME task into one display
  // row (pause / resume / re-enter each create a discrete session, #1897). We
  // show the summed total per task; tap a multi-session row to expand the
  // individual session times. Submission is unchanged — each session still ships
  // as its own CallLog row (the billing/audit truth; the timecard lists + sums
  // them). So this is display-only + it keys the per-task split-time input by
  // project (one input per task, not per session).
  interface TaskGroup {
    key: string;
    projectId: number;
    projectType: 'call' | 'set';
    projectName: string;
    clientName?: string;
    status: 'complete' | 'incomplete';
    sessions: ProjectSummary[];
    totalSeconds: number;
  }
  const groupedProjects = useMemo<TaskGroup[]>(() => {
    const groups = new Map<string, TaskGroup>();
    for (const p of projects) {
      const key = `${p.projectType}-${p.projectId}`;
      const existing = groups.get(key);
      if (existing) {
        existing.sessions.push(p);
        existing.totalSeconds += taskDurationSeconds(p);
        if (p.status === 'incomplete') existing.status = 'incomplete';
      } else {
        groups.set(key, {
          key,
          projectId: p.projectId,
          projectType: p.projectType,
          projectName: p.projectName,
          clientName: p.clientName,
          status: p.status,
          sessions: [p],
          totalSeconds: taskDurationSeconds(p),
        });
      }
    }
    return Array.from(groups.values());
  }, [projects]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // ----- #2104 Split Time allocation (gated by splitTimeEnabled) -----
  // Allocation is per TASK (grouped), not per session — one editable input per
  // task, matching the collapsed display (#3028).
  const splitEvenly = () => {
    const parts = evenSplitMinutes(calculateStoreTime(), groupedProjects.length);
    setAllocMinutes(Object.fromEntries(groupedProjects.map((g, i) => [g.projectId, parts[i]])));
    allocTouched.current = false;
  };
  const allocSum = allocMinutes ? Object.values(allocMinutes).reduce((a, b) => a + b, 0) : 0;
  const unallocatedMinutes = allocMinutes ? calculateStoreTime() - allocSum : 0;
  // Span edited while an untouched (purely even) allocation is active ->
  // re-split automatically, so "round to 1h then split" needs no second tap.
  useEffect(() => {
    if (allocMinutes && !allocTouched.current) {
      const parts = evenSplitMinutes(
        clampStoreMinutes(checkInTime, checkOutTime, totalTimeMinutes),
        groupedProjects.length
      );
      setAllocMinutes(Object.fromEntries(groupedProjects.map((g, i) => [g.projectId, parts[i]])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInTime, checkOutTime]);

  // Fetch manager history for this store
  useEffect(() => {
    if (isOpen && storeId) {
      storeManagersApi.getManagers(storeId)
        .then(managers => {
          setManagerOptions(managers);
        })
        .catch(err => {
          // Silently fail - manager autocomplete is optional
          console.debug('Manager history not available:', err);
        });
    }
  }, [isOpen, storeId]);

  // Initialize canvas when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCoordinates = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
  };

  const handleSelectManager = (name: string) => {
    setManagerName(name);
    setShowDropdown(false);
  };

  const handleComplete = async () => {
    if (!hasDrawn) {
      // #2092: when a signature is required there is no skip path at all —
      // the Complete button is disabled until signed, so this is just a belt.
      if (requiresSignature) return;
      // #3195 (#2959 follow-through, RMR 2026-07-14): a NON-LEAD team member
      // owes no signature AND no reason — the lead owns the paperwork; the
      // folded pad exists only as a fallback (lead's phone died). Complete
      // directly, zero prompts. Their checkout just populates the timesheet.
      if (isTeamProject && !isTeamLeader) {
        await submitCheckout(true);
        return;
      }
      // Legacy (flag-false, single-rep) path: no signature -> the reason modal.
      setShowNoSignatureModal(true);
      return;
    }

    await submitCheckout();
  };

  const submitCheckout = async (skipSignature = false) => {
    setIsLoading(true);

    let signatureDataUrl: string | null = null;
    let finalNoSignatureReason: string | null = null;

    if (!skipSignature && hasDrawn) {
      const canvas = canvasRef.current;
      if (canvas) {
        signatureDataUrl = canvas.toDataURL('image/png');
      }
    } else {
      // Empty -> null (a non-lead team checkout has NO reason by design, #3195);
      // the skip-with-reason paths always arrive here with a value selected.
      finalNoSignatureReason = (noSignatureReason === 'other' ? otherReason : noSignatureReason) || null;
    }

    // Save manager name to history if new.
    // Sync queue is the sole server-write path (offline-first migration #1983/#1985/#1987).
    if (managerName.trim()) {
      try {
        const { writeOps } = await import('../sync/writeOps');
        await writeOps.addStoreManager.apply({ storeId, managerName: managerName.trim() });
      } catch (enqErr) {
        console.warn('addStoreManager enqueue failed (non-fatal):', enqErr);
      }
    }

    const checkoutData: CheckoutData = {
      managerName: managerName.trim(),
      signatureDataUrl,
      noSignatureReason: finalNoSignatureReason,
      projects,
      totalTimeMinutes: calculateStoreTime(),  // Use calculated store time
      timestamp: new Date().toISOString(),
      isTeamProject,
      isTeamLeader: isTeamProject ? isTeamLeader : false,
      visitNotes: visitNotes.trim(),
      departureOdometer: departureOdometer.trim() === '' ? undefined : parseFloat(departureOdometer),
      // Include store times (editable)
      storeCheckInTime: checkInTime || undefined,
      storeCheckOutTime: checkOutTime || undefined,
      // #2104: balanced per-project allocation, or null for default mode.
      timeAllocation: allocMinutes
        ? groupedProjects.map((g) => ({ projectId: g.projectId, minutes: allocMinutes[g.projectId] ?? 0 }))
        : null,
    };

    setIsLoading(false);
    onComplete(checkoutData);
  };

  const handleNoSignatureSubmit = () => {
    if (!noSignatureReason || (noSignatureReason === 'other' && !otherReason.trim())) {
      return;
    }
    setShowNoSignatureModal(false);
    submitCheckout(true);
  };

  if (!isOpen) return null;

  // #2959 — on a team set where the current rep is NOT the lead, the manager
  // name + signature are the team lead's responsibility. Keep them fully
  // reachable (in case the lead's phone dies) but fold them away and make them
  // optional — same treatment the survey/deliverables get for a non-lead. Team
  // status is deterministic (#3022), so this needs no separate "locked" flag.
  const signatureOptional = isTeamProject && !isTeamLeader;

  // The manager-name + signature + legal-notice block, rendered inline for the
  // required case and inside a fold for the non-lead team case.
  const managerSignatureBlock = (
    <>
      {/* Manager Name Input with Dropdown — directly above the signature
          box (#2092, per RMR): the manager being named is the one signing. */}
      <div className="mb-4" ref={dropdownRef}>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Manager Name{' '}
          {/* Required whenever the project demands a signature (#2092), and
              on the legacy path whenever a signature is captured; only the
              legacy skip-with-reason path leaves it optional (#1996). */}
          {requiresSignature || hasDrawn ? (
            <span className="text-red-500">*</span>
          ) : (
            <span className="text-xs font-normal text-slate-400">(optional)</span>
          )}
        </label>
        <div className="relative">
          <input
            type="text"
            value={managerName}
            onChange={(e) => setManagerName(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder="Enter or select manager name"
            className="w-full rounded-lg border border-slate-200 px-4 py-3 pr-10 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
          >
            <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {/* Dropdown */}
          {showDropdown && managerOptions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {managerOptions
                .filter(opt => opt.manager_name.toLowerCase().includes(managerName.toLowerCase()))
                .map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleSelectManager(opt.manager_name)}
                    className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-slate-50"
                  >
                    <span className="text-slate-700">{opt.manager_name}</span>
                    <span className="text-xs text-slate-400">Used {opt.use_count}x</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Signature Canvas */}
      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-slate-700">
          Signature
          {requiresSignature && <span className="ml-1 text-red-500">*</span>}
        </label>
        <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white">
          <canvas
            ref={canvasRef}
            className="h-32 w-full cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          {!hasDrawn && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">
              Sign here
            </div>
          )}
        </div>
        {hasDrawn && (
          <button
            onClick={clearCanvas}
            className="mt-2 text-sm text-slate-500 hover:text-slate-700"
          >
            Clear signature
          </button>
        )}
      </div>

      {/* Legal Notice */}
      <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
        By signing above, the manager confirms that the visit was completed satisfactorily.
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <div className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white sm:max-w-lg sm:rounded-2xl">
        {/* Header — matches the app's PageHeader personality (primary gradient,
            font-heading, white-on-primary) instead of the old plain-white chrome
            that looked foreign to the rest of the UI (#2016 follow-up). */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-4 text-white">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm text-primary-200">{storeName}</p>
              <h2 className="font-heading text-xl font-bold">Store Visit Summary</h2>
            </div>
            <button
              onClick={onCancel}
              aria-label="Close"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20 transition-colors hover:bg-white/30"
            >
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Store Visit Times */}
          {(checkInTime || checkOutTime) && (
            <div className="mb-4 rounded-lg bg-slate-50 p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                Store Visit Time
                <span className="ml-1 normal-case font-normal text-slate-400">
                  {splitTimeEnabled ? '· tracked automatically, adjust only if needed' : '· tracked automatically'}
                </span>
              </div>
              <div className="space-y-2">
                {/* Check-in Time. #2104: editable native time picker only when the
                    Split Time gate is on; otherwise read-only tracked time (the app
                    only shows what it recorded — no misleading dead control). The
                    picker auto-saves on change; empty value is a no-op so we never
                    drop the stored ISO. (#1947 follow-up) */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Check-in</span>
                  {splitTimeEnabled ? (
                    <input
                      type="time"
                      aria-label="Check-in time"
                      value={isoToHHMM(checkInTime)}
                      onChange={(e) => {
                        const next = hhmmToISO(e.target.value, checkInTime);
                        if (next) setCheckInTime(next);
                      }}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-medium text-primary-700 focus:border-primary-500 focus:outline-none"
                    />
                  ) : (
                    <span className="text-sm font-medium text-slate-700" data-testid="checkin-readonly">
                      {formatClock(checkInTime) ?? '—'}
                    </span>
                  )}
                </div>

                {/* Check-out Time — same gating as check-in. */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Check-out</span>
                  {splitTimeEnabled ? (
                    <input
                      type="time"
                      aria-label="Check-out time"
                      value={isoToHHMM(checkOutTime)}
                      onChange={(e) => {
                        const next = hhmmToISO(e.target.value, checkOutTime);
                        if (next) setCheckOutTime(next);
                      }}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-medium text-primary-700 focus:border-primary-500 focus:outline-none"
                    />
                  ) : (
                    <span className="text-sm font-medium text-slate-700" data-testid="checkout-readonly">
                      {formatClock(checkOutTime) ?? '—'}
                    </span>
                  )}
                </div>

                {/* Total Store Time */}
                <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2">
                  <span className="text-sm font-medium text-slate-700">Total Store Time</span>
                  <span className="text-sm font-semibold text-slate-800">
                    {formatDuration(calculateStoreTime())}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Projects/Tasks List */}
          <div className="mb-4">
            <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
              {groupedProjects.length > 1 ? 'Tasks Completed' : 'Project'}
            </h3>
            <div className="space-y-2">
              {groupedProjects.map((group) => {
                const review = orderReview?.[group.projectId];
                const multi = group.sessions.length > 1;
                const isExpanded = expandedGroups.has(group.key);
                return (
                <div
                  key={group.key}
                  className={`rounded-lg px-3 py-2 ${
                    group.projectType === 'set' ? 'bg-purple-50' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {group.status === 'complete' ? (
                      <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      </svg>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                          group.projectType === 'set'
                            ? 'bg-purple-200 text-purple-800'
                            : 'bg-blue-200 text-blue-800'
                        }`}>
                          {group.projectType === 'set' ? 'Set' : 'Call'}
                        </span>
                        <p className="text-sm font-medium text-slate-700">
                          <span className="font-mono">#{group.projectId}</span> {group.projectName}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {group.clientName && <span>{group.clientName}</span>}
                        {multi ? (
                          // #3028 — collapsed: N sessions, tap to expand the times.
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.key)}
                            className="font-medium text-primary-600"
                            data-testid={`sessions-toggle-${group.projectId}`}
                          >
                            {group.sessions.length} sessions {isExpanded ? '▲' : '▼'}
                          </button>
                        ) : (
                          (() => {
                            // #2465: show the parseable side(s); never "Invalid Date".
                            const s = formatClock(group.sessions[0].startTime);
                            const e = formatClock(group.sessions[0].endTime);
                            if (!s && !e) return null;
                            return <span>{s ?? '?'}{e ? ` - ${e}` : ''}</span>;
                          })()
                        )}
                      </div>
                      {group.status === 'incomplete' && (
                        <p className="text-xs text-amber-600">Incomplete — your time is saved; finish on a return visit</p>
                      )}
                    </div>
                  </div>
                  {allocMinutes ? (
                    // #2104 allocation mode: editable minutes per task (group).
                    <span className="flex items-center gap-1 text-sm">
                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        aria-label={`Minutes for ${group.projectName}`}
                        value={allocMinutes[group.projectId] ?? 0}
                        onChange={(e) => {
                          allocTouched.current = true;
                          const v = Math.max(0, Math.round(Number(e.target.value) || 0));
                          setAllocMinutes((prev) => ({ ...(prev ?? {}), [group.projectId]: v }));
                        }}
                        className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-sm focus:border-primary-500 focus:outline-none"
                      />
                      <span className="text-slate-500">m</span>
                    </span>
                  ) : (
                    <span className={`text-sm font-medium ${
                      group.projectType === 'set' ? 'text-purple-700' : 'text-blue-700'
                    }`}>
                      {formatDurationSeconds(group.totalSeconds)}
                    </span>
                  )}
                  </div>

                  {/* #3028 — expanded per-session breakdown (the discrete rows
                      that still submit as separate CallLogs). */}
                  {multi && isExpanded && (
                    <div className="mt-2 space-y-1 border-t border-slate-200/70 pt-2" data-testid={`sessions-${group.projectId}`}>
                      {group.sessions.map((s, i) => {
                        const st = formatClock(s.startTime);
                        const en = formatClock(s.endTime);
                        return (
                          <div key={i} className="flex items-center justify-between text-xs text-slate-500">
                            <span>{st ?? '?'}{en ? ` - ${en}` : ''}</span>
                            <span className="font-medium">{formatDurationSeconds(taskDurationSeconds(s))}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* #2340 — orders / inventory / out-of-stock captured for THIS
                      project, so the manager reviews each task's work before signing. */}
                  {review?.hasAny && (
                    <div
                      className="mt-2 space-y-2 border-t border-slate-200/70 pt-2 text-xs"
                      data-testid={`task-review-${group.projectId}`}
                    >
                      {review.orders.length > 0 && (
                        <div>
                          <div className="mb-0.5 font-semibold text-slate-600">
                            Orders · {review.totalOrderUnits} {review.totalOrderUnits === 1 ? 'unit' : 'units'}
                          </div>
                          {review.orders.map((o) => (
                            <div key={`ord-${o.productid}`} className="flex items-center justify-between text-slate-700">
                              <span className="min-w-0 truncate">{o.description}</span>
                              <span className="ml-2 flex-shrink-0 font-medium">×{o.qty}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {review.inventory.length > 0 && (
                        <div>
                          <div className="mb-0.5 font-semibold text-slate-600">Inventory</div>
                          {review.inventory.map((o) => (
                            <div key={`inv-${o.productid}`} className="flex items-center justify-between text-slate-700">
                              <span className="min-w-0 truncate">{o.description}</span>
                              <span className="ml-2 flex flex-shrink-0 items-center gap-1 font-medium">
                                {/* #2340 — reps only count lows (3 or under); flag them so the
                                    manager sees what needs ordering through their warehouse. */}
                                {o.onHand <= LOW_STOCK_THRESHOLD && (
                                  <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-amber-700">
                                    Low
                                  </span>
                                )}
                                {o.onHand} on hand
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {review.oos.length > 0 && (
                        <div>
                          <div className="mb-0.5 font-semibold text-red-700">Out of stock</div>
                          {review.oos.map((o) => (
                            <div key={`oos-${o.productid}`} className="flex items-center gap-1.5 text-slate-700">
                              <span className="inline-block rounded bg-red-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-red-700">
                                OOS
                              </span>
                              <span className="min-w-0 truncate">{o.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            {/* Total Task Time — sum of the rows above (#1996), so the rendered
                rows always reconcile with the total the rep reads. */}
            <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
              <span className="text-sm font-medium text-slate-700">Total Task Time</span>
              <span className="text-sm font-semibold text-slate-800">
                {allocMinutes
                  ? formatDuration(allocSum)
                  : formatDurationSeconds(projects.reduce((sum, p) => sum + taskDurationSeconds(p), 0))}
              </span>
            </div>

            {/* #2104 Split Time (gated): opt-in even allocation of the onsite span
                across the tasks (the commingled-endcap workflow). Re-tappable as
                the fixer after any edit. Only shown when the feature gate is on. */}
            {splitTimeEnabled && groupedProjects.length >= 2 && (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <button
                  type="button"
                  data-testid="split-time-button"
                  onClick={splitEvenly}
                  className="w-full rounded-lg border border-primary-200 bg-primary-50 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
                >
                  Split Time Evenly Across Tasks
                </button>
                {allocMinutes && unallocatedMinutes !== 0 && (
                  <p className="mt-2 text-center text-xs font-medium text-amber-600" data-testid="unallocated-indicator">
                    {unallocatedMinutes > 0
                      ? `Unallocated: ${unallocatedMinutes}m`
                      : `Over-allocated: ${-unallocatedMinutes}m`}{' '}
                    — task times must sum to the {formatDuration(calculateStoreTime())} onsite total
                  </p>
                )}
                {allocMinutes && unallocatedMinutes === 0 && (
                  <p className="mt-2 text-center text-xs text-slate-500" data-testid="allocation-balanced">
                    Time evenly allocated — tasks sum to the onsite total
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Warning for incomplete projects */}
          {incompleteCount > 0 && (
            <div className="mb-4 rounded-lg bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {incompleteCount} project{incompleteCount > 1 ? 's' : ''} incomplete
                  </p>
                  <p className="text-xs text-amber-700">Progress has been saved.</p>
                </div>
              </div>
            </div>
          )}

          {/* #2959 / #3022 — TEAM PROJECT is DETERMINISTIC from the set
              assignment: a READ-ONLY indicator, never a rep-editable toggle, and
              hidden entirely for a non-team visit (calls / solo sets). The
              team-lead line is driven by the same resolved state. */}
          {isTeamProject && (
            <div className="mb-4" data-testid="team-project-indicator">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Team Project
              </label>
              <div className="flex items-center gap-2 rounded-lg bg-primary-50 px-4 py-2.5">
                <span className="inline-flex h-2 w-2 rounded-full bg-primary-500" aria-hidden />
                <span className="text-sm font-medium text-primary-700">
                  {isTeamLeader ? 'Team project — you are the team lead' : 'Team project — you are a team member'}
                </span>
              </div>
              {!isTeamLeader && (
                <p className="mt-1 text-xs text-slate-500">
                  The team lead completes the manager signature and shared deliverables.
                </p>
              )}
            </div>
          )}

          {/* Odometer on the way out. Pre-filled with the rep's last reading —
              the vehicle hasn't moved while they were inside — so the normal
              case is one tap, and this reading is where the next drive starts. */}
          {lastOdometer != null && (
            <div className="mb-4">
              <label htmlFor="departure-odometer" className="mb-1 block text-sm font-medium text-slate-700">
                Odometer leaving
              </label>
              <div className="relative">
                <input
                  id="departure-odometer"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={departureOdometer}
                  onChange={(e) => setDepartureOdometer(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-4 py-3 pr-12 font-mono text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">mi</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {departureOdometer !== '' && parseFloat(departureOdometer) > lastOdometer
                  ? `${(parseFloat(departureOdometer) - lastOdometer).toFixed(1)} mi since you arrived — starts your next drive here.`
                  : 'Starts your next drive. Edit only if you drove while you were here.'}
              </p>
            </div>
          )}

          {/* Visit Notes */}
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Visit Notes <span className="text-xs font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              value={visitNotes}
              onChange={(e) => setVisitNotes(e.target.value)}
              placeholder="Add any notes about this visit..."
              rows={2}
              className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
            />
          </div>

          {/* #2959 — a non-lead on a team set gets the manager signature folded
              away (optional but reachable); everyone else sees it inline. */}
          {signatureOptional ? (
            <details className="mb-4 rounded-lg border border-slate-200" data-testid="manager-signature-fold">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-600">
                Manager signature — handled by the team lead
                <span className="ml-1 block text-xs font-normal text-slate-400">
                  Tap to open only if you need to sign for a lead who can't.
                </span>
              </summary>
              <div className="px-4 pb-4">{managerSignatureBlock}</div>
            </details>
          ) : (
            managerSignatureBlock
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4">
          {/* No Signature Option — only when the project does NOT require a
              signature (#2092). On required projects there is no skip path. */}
          {!requiresSignature && (
            <button
              type="button"
              onClick={() => setShowNoSignatureModal(true)}
              className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 py-3 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Skip Signature - Manager Not Available
            </button>
          )}
          {/* Inline hint while the required signature is missing (#2092). */}
          {requiresSignature && !hasDrawn && (
            <p className="mb-3 text-center text-xs text-slate-500" data-testid="signature-required-hint">
              A manager signature is required to complete this visit.
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 rounded-lg border border-slate-200 py-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleComplete}
              // #2092 required path: name + signature both mandatory. Legacy
              // path (#1996): manager name required only when signed — an
              // unsigned tap routes to the skip-signature modal (reason
              // required there instead).
              disabled={
                isLoading ||
                (requiresSignature ? !hasDrawn || !managerName.trim() : hasDrawn && !managerName.trim()) ||
                // #2104: an engaged allocation must balance before completing.
                (allocMinutes !== null && unallocatedMinutes !== 0)
              }
              className="flex-1 rounded-lg bg-primary-600 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Processing...' : 'Complete Visit'}
            </button>
          </div>
        </div>
      </div>

      {/* No Signature Reason Modal */}
      {showNoSignatureModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-4">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">Signature Required</h3>
            <p className="mb-4 text-sm text-slate-600">
              A manager signature is required to complete this visit. If unable to obtain signature, please provide a reason:
            </p>
            <div className="mb-4 space-y-2">
              {NO_SIGNATURE_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
                >
                  <input
                    type="radio"
                    name="noSignatureReason"
                    value={reason.value}
                    checked={noSignatureReason === reason.value}
                    onChange={(e) => setNoSignatureReason(e.target.value)}
                    className="h-4 w-4 text-primary-600"
                  />
                  <span className="text-sm text-slate-700">{reason.label}</span>
                </label>
              ))}
            </div>
            {noSignatureReason === 'other' && (
              <input
                type="text"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Please specify..."
                className="mb-4 w-full rounded-lg border border-slate-200 px-4 py-2 text-sm placeholder-slate-400 focus:border-primary-500 focus:outline-none"
              />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowNoSignatureModal(false);
                  setNoSignatureReason('');
                  setOtherReason('');
                }}
                className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                onClick={handleNoSignatureSubmit}
                disabled={!noSignatureReason || (noSignatureReason === 'other' && !otherReason.trim())}
                className="flex-1 rounded-lg bg-amber-500 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
              >
                Complete Without Sig
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
